import { useCallback, useState } from 'react';

export interface ConfirmDeleteOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  size?: 'sm' | 'lg' | 'xl';
}

export interface ConfirmDeleteState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  variant: 'danger' | 'warning' | 'info';
  size: 'sm' | 'lg' | 'xl';
}

const defaultOptions: ConfirmDeleteOptions = {
  title: 'Confirm Delete',
  message: 'Are you sure you want to delete this item? This action cannot be undone.',
  confirmText: 'Delete',
  cancelText: 'Cancel',
  variant: 'danger',
  size: 'lg',
};

export default function useConfirmDelete(
  onConfirm?: () => void | Promise<void>,
  options: ConfirmDeleteOptions = {}
) {
  const [state, setState] = useState<ConfirmDeleteState>({
    isOpen: false,
    ...defaultOptions,
    ...options,
  } as ConfirmDeleteState);

  const [isLoading, setIsLoading] = useState(false);

  const openDialog = useCallback((customOptions?: ConfirmDeleteOptions) => {
    setState(prev => ({
      ...prev,
      isOpen: true,
      ...customOptions,
    }));
  }, []);

  const closeDialog = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
    setIsLoading(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!onConfirm) {
      closeDialog();
      return;
    }

    try {
      setIsLoading(true);
      await onConfirm();
      closeDialog();
    } catch (error) {
      console.error('Error during delete confirmation:', error);
      setIsLoading(false);
      // Keep dialog open on error so user can retry
    }
  }, [onConfirm, closeDialog]);

  const handleCancel = useCallback(() => {
    closeDialog();
  }, [closeDialog]);

  return {
    // State
    isOpen: state.isOpen,
    isLoading,
    title: state.title,
    message: state.message,
    confirmText: state.confirmText,
    cancelText: state.cancelText,
    variant: state.variant,
    size: state.size,
    
    // Actions
    openDialog,
    closeDialog,
    handleConfirm,
    handleCancel,
    
    // Convenience methods
    confirm: handleConfirm,
    cancel: handleCancel,
  };
}
